import { types, flow, getRoot } from "mobx-state-tree"
import * as ParkingTicketService from "~/services/parking/ticket"
import ParkingTicket from "~/stores/models/ParkingTicket"
import Network from "~/stores/models/Network"
import { getParkingFees } from "~/services/payments"
import { PAYMENT_PROVIDERS } from "~/utilities/constants";

const UserFromRobotTickets = types
  .model("UserFromRobotTickets", {
    list: types.array(ParkingTicket),
    selectedTickets: types.array(types.safeReference(ParkingTicket)),
    ticketsService: types.optional(Network, {}),
    submitted: types.optional(types.boolean, false),
    serviceFee: types.maybeNull(types.number, 0),
    additionalFeePerTicket: types.maybeNull(types.number, 0),
    creditCardFee: types.maybeNull(types.number, 0)
  })
  .views((self) => ({
    get allTicketsSelected() {
      const selected = new Set([
        ...self.selectedTickets.filter(Boolean).map((item) => item.objectId)
      ])
      return (
        self.list.reduce((allSelected, item) => {
          return allSelected && selected.has(item.objectId)
        }, true) &&
        self.list.length &&
        self.selectedTickets.length > 0
      )
    },
    isSelected(ticket) {
      return !!self.selectedTickets.filter(Boolean).find((item) => {
        return ticket.ticketID === item.ticketID
      })
    },
    get selectedFineAmount() {
      const sum = self.selectedTickets.filter(Boolean).reduce((amount, ticket) => {
        const fineAmount = ticket.fineAmount || ticket.currentFine
        return amount + Number(fineAmount)
      }, 0)
      return sum.toFixed(2)
    },
    get user() {
      const root = getRoot(self)
      return root.auth.user
    },
    get paySubtotal() {
      const { user } = self
      const userCredits = user.credits ? user.credits : 0
      const sum =
        self.totalServiceFee +
        Number(self.feeForSelectedFineAmount) +
        Number(self.selectedFineAmount) -
        userCredits
      return sum < 0 ? 0 : sum
    },
    get totalServiceFee() {
      return self.serviceFee + self.additionalTicketsFee
    },
    get additionalTicketsFee() {
      if (self.selectedTickets.length <= 0) return 0
      return (self.selectedTickets.length - 1) * self.additionalFeePerTicket
    },
    get feeForSelectedFineAmount() {
      return (self.selectedFineAmount * self.creditCardFee).toFixed(2)
    },
    get toPayFineAmount() {
      const sum = self.selectedTickets.filter(Boolean).reduce((amount, ticket) => {
        const fineAmount = ticket.fineAmount || ticket.currentFine
        return amount + Number(fineAmount)
      }, 0)
      return (sum / 2).toFixed(2)
    }
  }))
  .actions((self) => ({
    setTickets(tickets, forcePay) {
      if (!forcePay || forcePay === "false") { // Fight flow
        // Hides non fightable tickets that are returned as fromRobot tickets to prevent users from fighting them atm
        self.list = tickets.filter(t => {
          return typeof t.ticketID === "string" &&
            !t.ticketID.startsWith("3") &&
            !t.ticketID.startsWith("4") &&
            !t.ticketID.startsWith("5")
        })
      } else { // Pay flow
        self.list = tickets.filter(t => {
          return typeof t.ticketID === "string"
        })
      }
    },
    setServiceFee(serviceFee) {
      self.serviceFee = serviceFee
    },
    setAdditionalFeePerTicket(additionalFeePerTicket) {
      self.additionalFeePerTicket = additionalFeePerTicket
    },
    setCreditCardFee(creditCardFee) {
      self.creditCardFee = creditCardFee
    },
    reset() {
      self.submitted = false
      self.clearTickets()
    },
    clearTickets() {
      self.selectedTickets.clear()
      self.list.clear()
    },
    ticketsSubmitted() {
      self.submitted = true
      // if it has been submitted reset the tickets to remove stale stuff
      self.clearTickets()
    },
    toggleAllTickets() {
      if (self.allTicketsSelected) {
        self.selectedTickets.clear()
      } else {
        self.selectedTickets = self.list.slice()
      }
    },
    toggleTicket(ticket) {
      if (self.isSelected(ticket)) {
        self.selectedTickets = self.selectedTickets.filter(Boolean).filter((item) => {
          return item.ticketID !== ticket.ticketID
        })
      } else {
        self.selectedTickets.push(ticket)
      }
    },
    initialize: flow(function* initialize() {
      const root = getRoot(self)
      const { isPayNYCFlow } = root.ui
      const source = isPayNYCFlow ? "payNYC" : "webapp"
      const totalAmountDue = self.selectedTickets.filter(Boolean).map((ticket) => ticket.amountDue)
      const pricing = yield getParkingFees(null, source, totalAmountDue,self.user.paymentProcessor.type === PAYMENT_PROVIDERS.evervault)
      self.setServiceFee(pricing.serviceFee)
      self.setAdditionalFeePerTicket(pricing.additionalFeePerTicket)
      self.setCreditCardFee(pricing.creditCardFee)
      return pricing
      }),
    fetchRobotTickets: flow(function* fetch(userId, batchNumber, forcePay) {
      let success = false
      try {
        if (self.ticketsService.isLoading) {
          return false
        }
        self.ticketsService.requestStart()
        const robotTickets = yield ParkingTicketService.getFightRobotTickets(
          userId,
          batchNumber
        )
        self.setTickets(robotTickets.results,forcePay)
        self.list.forEach((ticket) => self.selectedTickets.push(ticket))
        success = true
        self.ticketsService.requestComplete()
      } catch (error) {
        if (process.env.WINIT_ENV !== "test") {
          console.error("error fetching users robot tickets", error)
        }
        success = false
        self.ticketsService.requestError(error)
        self.showErrorToast()
      }
      return success
    }),
    payRobotTickets: flow(function* fight(userId) {
      let success = false
      try {
        const ticketIds = self.selectedTickets.filter(Boolean).map((ticket) => ticket.ticketID)
        if (self.ticketsService.isLoading) {
          return false
        }
        self.ticketsService.requestStart()
        const robotTickets = yield ParkingTicketService.payRobotTickets(
          userId,
          ticketIds
        )
        self.setTickets(robotTickets.result)
        success = true
        self.ticketsService.requestComplete()
      } catch (error) {
        if (process.env.WINIT_ENV !== "test") {
          console.error("error fetching users robot tickets", error)
        }
        success = false
        self.ticketsService.requestError(error)
        self.showErrorToast()
      }
      return success
    }),
    fightRobotTickets: flow(function* fight(userId) {
      let success = false
      try {
        const ticketIds = self.selectedTickets.filter(Boolean).map((ticket) => ticket.ticketID)
        if (self.ticketsService.isLoading) {
          return false
        }
        self.ticketsService.requestStart()
        const robotTickets = yield ParkingTicketService.fightRobotTickets(
          userId,
          ticketIds
        )
        self.setTickets(robotTickets.result)
        success = true
        self.ticketsSubmitted()
        self.ticketsService.requestComplete()
      } catch (error) {
        if (process.env.WINIT_ENV !== "test") {
          console.error("error fetching users robot tickets", error)
        }
        success = false
        self.ticketsService.requestError(error)
        self.showErrorToast()
      }
      return success
    }),
    showErrorToast() {
      const rootStore = getRoot(self)
      rootStore.ui.toasts.addToast({
        message: self.ticketsService.error,
        type: "ERROR"
      })
    }
  }))

const UserRobotTickets = types.compose(UserFromRobotTickets)

export default UserRobotTickets
